// ============================================================================
//  NarrativeTable.js — the "Documentation Sets" tab.
// ----------------------------------------------------------------------------
//  A narrative is ONE hypothetical patient. Four sections that MUST match:
//    complaint · history · examination · other documentation (impression).
//  Auditors pick a narrative later and all 4 sections stamp into the claim
//  as one internally-consistent story — no more mixing an adult complaint
//  with a paediatric exam by accident.
//
//  Data lives in condition_narratives (4 text columns + label + rationale).
//  AI generates matched sets via /narratives/suggest.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  templateNarratives, templateNarrativeRow, templateNarrativeSuggest,
} from '../../BaseURL';
import notify from './notify';
import { IconPlus, IconSparkle, IconEdit, IconTrash, IconChevron } from './icons';
import NarrativeLabs from './NarrativeLabs';
import NarrativeMedications from './NarrativeMedications';
import NarrativeScans from './NarrativeScans';

const SECTIONS = [
  { key: 'complaint',   label: 'Complaint',
    placeholder: 'Patient presents with a 3-day history of…' },
  { key: 'history',     label: 'History',
    placeholder: 'No prior admissions. Non-smoker. Father with hypertension. No known drug allergies…' },
  { key: 'examination', label: 'Physical Examination',
    placeholder: 'Patient appears unwell. Mild pallor of conjunctiva. Chest clear. Spleen palpable 2 cm below…' },
  { key: 'otherDocs',   label: 'Other Documentation',
    placeholder: 'Working diagnosis: uncomplicated malaria. No severity features. Differentials considered…' },
];
// Map API field names ↔ form keys (form uses camelCase; row uses snake_case).
const rowToForm = (r) => ({
  label:       r?.label || '',
  complaint:   r?.complaint || '',
  history:     r?.history || '',
  examination: r?.examination || '',
  otherDocs:   r?.other_docs || '',
  isDefault:   !!r?.is_default,
});

export default function NarrativeTable({
  conditionId, conditionAgeScope, onAiSuggested,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | row
  const [suggesting, setSuggesting] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(templateNarratives(conditionId));
      const list = data.rows || [];
      setRows(list);
      // Auto-expand the first narrative so the labs section is discoverable
      // without having to hunt for the chevron.
      setExpandedId((prev) => prev ?? list[0]?.id ?? null);
    } catch (err) {
      notify.error('Failed to load', err?.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [conditionId]);

  useEffect(() => { load(); }, [load]);

  const remove = async (row) => {
    const ok = await notify.confirm('Remove this narrative?', `"${row.label}" will be removed from the library.`, {
      confirmText: 'Remove', cancelText: 'Cancel', danger: true,
    });
    if (!ok) return;
    try {
      await axios.delete(templateNarrativeRow(conditionId, row.id));
      notify.toast.success('Removed');
      load();
    } catch (err) {
      notify.error('Delete failed', err?.response?.data?.error || err.message);
    }
  };

  const suggest = async () => {
    setSuggesting(true);
    try {
      const { data } = await axios.post(templateNarrativeSuggest(conditionId), {
        ageScope: conditionAgeScope || 'both',
        count: 8,
      });
      notify.toast.success(`${data.inserted} narrative sets ready — review below`);
      onAiSuggested?.();
    } catch (err) {
      notify.error('AI suggest failed', err?.response?.data?.error || err.message);
    } finally {
      setSuggesting(false);
    }
  };

  return (
    <>
      <div className="tp-tab-toolbar">
        <div className="tp-tab-title">
          <strong>Documentation Sets</strong>
          <span className="tp-hint">
            One row = one internally-consistent case. Complaint · History · Examination · Other Documentation always go together.
          </span>
        </div>
        <div className="tp-tab-actions">
          <button className="tp-btn tp-btn-ghost" onClick={suggest} disabled={suggesting}>
            <IconSparkle size={14} />
            {suggesting ? 'Asking AI…' : 'Suggest with AI'}
          </button>
          <button className="tp-btn tp-btn-primary" onClick={() => setEditing('new')}>
            <IconPlus size={14} /> Add narrative
          </button>
        </div>
      </div>

      {loading && <div className="tp-empty">Loading…</div>}

      {!loading && rows.length === 0 && (
        <div className="tp-empty">
          <h3>No documentation sets yet</h3>
          <p>Add one manually, or press "Suggest with AI" — matched sets get generated and staged for your review.</p>
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div className="tp-narr-list">
          {rows.map((r) => {
            const isOpen = expandedId === r.id;
            return (
              <div key={r.id} className={`tp-narr-card ${isOpen ? 'is-open' : ''}`}>
                <button
                  type="button"
                  className="tp-narr-head"
                  onClick={() => setExpandedId(isOpen ? null : r.id)}
                >
                  <span className={`tp-chevron ${isOpen ? 'is-open' : ''}`}>
                    <IconChevron size={14} />
                  </span>
                  <div className="tp-narr-title">
                    <strong>{r.label}</strong>
                    <span className="tp-hint">
                      Used {r.use_count}×
                      {typeof r.lab_count === 'number' && (
                        <> · <span className="tp-narr-labcount">{r.lab_count} lab{r.lab_count === 1 ? '' : 's'}</span></>
                      )}
                      {typeof r.med_count === 'number' && (
                        <> · <span className="tp-narr-labcount">{r.med_count} med{r.med_count === 1 ? '' : 's'}</span></>
                      )}
                      {typeof r.scan_count === 'number' && r.scan_count > 0 && (
                        <> · <span className="tp-narr-labcount">{r.scan_count} scan{r.scan_count === 1 ? '' : 's'}</span></>
                      )}
                    </span>
                  </div>
                  <div className="tp-narr-actions" onClick={(e) => e.stopPropagation()}>
                    <button className="tp-icon-btn" onClick={() => setEditing(r)} title="Edit">
                      <IconEdit size={14} />
                    </button>
                    <button className="tp-icon-btn danger" onClick={() => remove(r)} title="Delete">
                      <IconTrash size={14} />
                    </button>
                  </div>
                </button>
                {isOpen && (
                  <div className="tp-narr-body">
                    {SECTIONS.map((s) => {
                      const val = s.key === 'otherDocs' ? r.other_docs : r[s.key];
                      return (
                        <div key={s.key} className="tp-narr-section">
                          <div className="tp-narr-section-label">{s.label}</div>
                          <div className="tp-narr-section-body">
                            {val || <em className="tp-muted">—</em>}
                          </div>
                        </div>
                      );
                    })}
                    <NarrativeLabs narrativeId={r.id} narrativeLabel={r.label} />
                    <NarrativeMedications narrativeId={r.id} />
                    <NarrativeScans narrativeId={r.id} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <NarrativeEditor
          conditionId={conditionId}
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </>
  );
}

/* ── Narrative editor — 4 stacked textareas ───────────────────────────── */
function NarrativeEditor({ conditionId, initial, onClose, onSaved }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(rowToForm(initial));
  const [saving, setSaving] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.label.trim().length < 1) {
      notify.toast.error('Label is required');
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await axios.put(templateNarrativeRow(conditionId, initial.id), form);
      } else {
        await axios.post(templateNarratives(conditionId), form);
      }
      notify.toast.success(isEdit ? 'Narrative updated' : 'Narrative added');
      onSaved?.();
    } catch (err) {
      notify.error('Save failed', err?.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="tp-modal-scrim" onClick={onClose}>
      <div className="tp-modal tp-modal-huge" onClick={(e) => e.stopPropagation()}>
        <div className="tp-modal-head-bar">
          <div>
            <h3>{isEdit ? 'Edit documentation set' : 'New documentation set'}</h3>
            <p className="tp-modal-sub">
              All four sections describe the same patient. Keep them internally consistent — that's the whole point of a matched set.
            </p>
          </div>
        </div>

        <form onSubmit={submit} className="tp-editor-body tp-narr-form">
          <div className="tp-field">
            <label>Label (short summary)</label>
            <input className="tp-input" autoFocus value={form.label}
              placeholder="e.g. Fever + splenomegaly, mildly dehydrated"
              onChange={(e) => update('label', e.target.value)} />
          </div>

          {SECTIONS.map((s) => (
            <div key={s.key} className="tp-field">
              <label>{s.label}</label>
              <textarea className="tp-input" rows={4} value={form[s.key]}
                placeholder={s.placeholder}
                onChange={(e) => update(s.key, e.target.value)} />
            </div>
          ))}

          <div className="tp-field">
            <label className="tp-check-row">
              <input type="checkbox" checked={form.isDefault}
                onChange={(e) => update('isDefault', e.target.checked)} />
              Auto-tick this narrative by default in the composer
            </label>
          </div>

          <div className="tp-modal-actions">
            <button type="button" className="tp-btn tp-btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="tp-btn tp-btn-primary" disabled={saving}>
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Add narrative'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
